import { Suspense, useContext, useEffect, useState } from "react";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";

import { AuthContext } from "../hooks/AuthContext";
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from "../theme";
import AppBreadcrumbs from "./AppBreadcrumbs";
import ColorModeToggle from "./ColorModeToggle";
import UserMenu from "./UserMenu";
import { visibleSections } from "./navigation";
import CciLogo from "../assets/cci-logo.svg";

const COLLAPSE_STORAGE_KEY = "cci.nav.collapsed";

export default function AppLayout() {
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true"
  );

  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // Route changes should never leave the overlay drawer covering the page.
  useEffect(() => setMobileOpen(false), [pathname]);

  const sections = visibleSections(user?.userRole);
  const showLabels = !isDesktop || !collapsed;
  const drawerWidth = showLabels ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED;

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar
        sx={{
          gap: 1.5,
          px: showLabels ? 2.5 : 1.5,
          justifyContent: showLabels ? "flex-start" : "center",
          minHeight: 64,
        }}
      >
        <Box
          component="img"
          src={CciLogo}
          alt=""
          sx={{ width: 28, height: 28, flexShrink: 0 }}
        />
        {showLabels && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ lineHeight: 1.2 }}>
              CCI Portal
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Network Services
            </Typography>
          </Box>
        )}
      </Toolbar>
      <Divider />

      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}>
        {sections.map((section, index) => (
          <List
            key={section.heading ?? `section-${index}`}
            dense
            sx={{ px: 1 }}
            subheader={
              section.heading && showLabels ? (
                <ListSubheader
                  disableSticky
                  sx={{
                    bgcolor: "transparent",
                    typography: "overline",
                    color: "text.secondary",
                    lineHeight: 2.5,
                  }}
                >
                  {section.heading}
                </ListSubheader>
              ) : undefined
            }
          >
            {section.items.map(({ label, path, icon: Icon }) => {
              const selected =
                pathname === path || pathname.startsWith(`${path}/`);
              return (
                <ListItemButton
                  key={path}
                  component={RouterLink}
                  to={path}
                  selected={selected}
                  aria-current={selected ? "page" : undefined}
                  sx={[
                    {
                      mb: 0.25,
                      minHeight: 42,
                      justifyContent: showLabels ? "flex-start" : "center",
                      px: showLabels ? 1.5 : 1,
                      "&.Mui-selected": {
                        bgcolor: "action.selected",
                        color: "primary.main",
                        "& .MuiListItemIcon-root": { color: "primary.main" },
                        "&:hover": { bgcolor: "action.selected" },
                      },
                    },
                    // `primary.main` is the fill colour, not the on-dark text
                    // colour; see the note in theme/palette.ts.
                    theme.applyStyles("dark", {
                      "&.Mui-selected": {
                        color: theme.palette.primary.light,
                        "& .MuiListItemIcon-root": {
                          color: theme.palette.primary.light,
                        },
                      },
                    }),
                  ]}
                >
                  <Tooltip
                    title={showLabels ? "" : label}
                    placement="right"
                    disableHoverListener={showLabels}
                  >
                    <ListItemIcon sx={{ minWidth: showLabels ? 36 : 0 }}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                  </Tooltip>
                  {showLabels && (
                    <ListItemText
                      primary={label}
                      slotProps={{
                        primary: {
                          variant: "body2",
                          sx: { fontWeight: selected ? 600 : 500 },
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>

      {isDesktop && (
        <>
          <Divider />
          <Box sx={{ p: 1, display: "flex", justifyContent: showLabels ? "flex-end" : "center" }}>
            <Tooltip title={collapsed ? "Expand navigation" : "Collapse navigation"}>
              <IconButton
                size="small"
                onClick={() => setCollapsed((value) => !value)}
                aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
              >
                {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: 8,
          top: -64,
          zIndex: (t) => t.zIndex.tooltip + 1,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          typography: "body2",
          textDecoration: "none",
          "&:focus": { top: 8 },
        }}
      >
        Skip to main content
      </Box>

      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          transition: theme.transitions.create(["width", "margin-left"], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: { xs: 56, md: 64 } }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            sx={{ display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AppBreadcrumbs />
          </Box>
          <ColorModeToggle />
          <UserMenu />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="Main navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderRight: 1,
              borderColor: "divider",
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: 1,
          outline: "none",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, md: 64 } }} />
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, maxWidth: 1600, width: "100%", mx: "auto" }}>
          <Suspense fallback={<LinearProgress sx={{ borderRadius: 1 }} />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
